import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @fileoverview ReasonRow — LOCI · CLT For You list row (FRE-773).
 *
 * 72×72 cover on the left, Space Grotesk title, venue · time meta, and an
 * italic teal reason pill on the middle, with a mono distance on the right.
 * The whole row is a single anchor — keyboard reachable via Tab, Enter opens
 * the destination.
 *
 * Hover raises the background to skyline teal `rgba(14,123,123,0.06)` per
 * the LOCI · CLT spec; active state darkens slightly. Reason badge reuses
 * the established VibeTag teal tokens (`#4FC4C4` text on
 * `rgba(79,196,196,0.12)` bg) for design-system consistency.
 */
import React, { forwardRef } from 'react';
import { useTheme } from '../../../theme';
const TEAL = '#4FC4C4';
const TEAL_BG = 'rgba(79, 196, 196, 0.12)';
// Hover / active backgrounds — skyline teal at 6% / 10% opacity per LOCI · CLT
// spec. Applied via Tailwind arbitrary-value utilities below.
const COVER_SIZE = 72;
const DefaultLink = React.forwardRef(function DefaultLink({ href, children, ...rest }, ref) {
    return (_jsx("a", { ref: ref, href: href, ...rest, children: children }));
});
export const ReasonRow = forwardRef(({ id: _id, href, title, venue, time, distance, imageUrl, reasonText, linkComponent: LinkComponent = DefaultLink, className, 'data-testid': dataTestId = 'reason-row', ...rest }, ref) => {
    const theme = useTheme();
    const rootClasses = [
        'group flex w-full items-center no-underline',
        'hover:bg-[rgba(14,123,123,0.06)] active:bg-[rgba(14,123,123,0.10)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        'motion-safe:transition-colors motion-safe:duration-150',
        className,
    ]
        .filter(Boolean)
        .join(' ');
    const rowStyle = {
        gap: theme.spacing.md,
        padding: `${theme.spacing.sm}px ${theme.spacing.xs}px`,
        borderRadius: theme.borderRadius.sm,
        color: 'inherit',
        // `group`/`hover` classes drive background; defaults are explicit so
        // Tailwind's JIT `hover:` / `active:` overrides apply without being
        // overridden by inline `background`.
        background: 'transparent',
    };
    const coverStyle = {
        width: COVER_SIZE,
        height: COVER_SIZE,
        flexShrink: 0,
        borderRadius: theme.borderRadius.sm,
        background: imageUrl
            ? `center / cover no-repeat url(${JSON.stringify(imageUrl)})`
            : theme.colors.surfaceAlt,
        backgroundColor: theme.colors.surfaceAlt,
    };
    const bodyStyle = {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    };
    const titleStyle = {
        margin: 0,
        fontFamily: theme.typography.fontFamilyDisplay ?? theme.typography.fontFamily,
        fontSize: theme.typography.fontSize.body,
        fontWeight: theme.typography.fontWeight.semibold,
        color: theme.colors.text,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        letterSpacing: '-0.005em',
    };
    const metaStyle = {
        margin: 0,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.fontSize.small,
        color: theme.colors.textMuted,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    };
    const badgeStyle = {
        display: 'inline-block',
        maxWidth: '100%',
        padding: '2px 8px',
        borderRadius: 9999,
        background: TEAL_BG,
        color: TEAL,
        fontStyle: 'italic',
        fontFamily: theme.typography.fontFamily,
        fontSize: '12px',
        lineHeight: 1.3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    };
    const distanceStyle = {
        flexShrink: 0,
        marginLeft: theme.spacing.sm,
        fontFamily: theme.typography.fontFamilyMono,
        fontSize: '12px',
        letterSpacing: '0.04em',
        color: theme.colors.textMuted,
        fontFeatureSettings: "'tnum' 1, 'zero' 1",
    };
    const reasonSegment = reasonText ? (_jsxs(_Fragment, { children: [_jsx("span", { "aria-hidden": "true", children: " \u00B7 " }), _jsx("span", { children: reasonText })] })) : null;
    return (_jsxs(LinkComponent, { ref: ref, href: href, "aria-label": reasonText ? `${title}, ${distance} — ${reasonText}` : `${title}, ${distance}`, className: rootClasses, style: rowStyle, "data-testid": dataTestId, ...rest, children: [_jsx("span", { "aria-hidden": "true", style: coverStyle, "data-testid": "reason-row-cover" }), _jsxs("span", { style: bodyStyle, children: [_jsx("span", { style: titleStyle, "data-testid": "reason-row-title", children: title }), _jsxs("span", { style: metaStyle, "data-testid": "reason-row-meta", children: [_jsx("span", { children: venue }), _jsx("span", { "aria-hidden": "true", children: " \u00B7 " }), _jsx("span", { children: time }), _jsx("span", { className: "sr-only", children: reasonSegment })] }), reasonText ? (_jsx("span", { "aria-hidden": "true", style: badgeStyle, "data-testid": "reason-row-badge", title: reasonText, children: reasonText })) : null] }), _jsx("span", { style: distanceStyle, "data-testid": "reason-row-distance", children: distance })] }));
});
ReasonRow.displayName = 'ReasonRow';
